import dayjs from 'dayjs';
import { ComplianceDocumentSummary } from '../components/types';

/**
 * A SkySlope checklist item: the checklist activity plus every ingestion
 * document that belongs to it. The agent acts at this level — ignore-and-resubmit
 * marks the whole activity resubmitted — so the redesigned page selects a
 * checklist item and shows its documents as tabs.
 *
 * Documents without a SkySlope activity (`skyslopeActivityId == null`) each
 * become their own single-document item.
 */
export interface ChecklistItem {
  /** Group key: the `skyslopeActivityId`, or the lone document id when ungrouped. */
  id: string;
  /** Display title: the checklist activity name, or the document name when ungrouped. */
  title: string;
  /**
   * True when this item maps to a real SkySlope checklist activity
   * (`skyslopeActivityId` present); false for a standalone document that isn't
   * attached to any checklist requirement. Drives the "Not on checklist"
   * treatment in the Other-documents list.
   */
  isChecklistItem: boolean;
  /** The documents under this checklist item, in `documentUpdatedAt` desc order. */
  docs: ComplianceDocumentSummary[];
  /**
   * True when ANY document under this item needs the agent's review (rejected,
   * not yet resubmitted). Drives the "Needs review" vs "Other documents" split.
   */
  needsReview: boolean;
  /**
   * The most recent `agentResubmittedAt` among the item's documents, or null if
   * none have been resubmitted. Gates the "Resubmitted" disabled action.
   */
  resubmittedAt: string | null;
}

export interface ChecklistItems {
  /** Every checklist item, in first-appearance (most-recent-first) order. */
  items: ChecklistItem[];
  /** Items that need the agent's review — the left rail "Needs review" list. */
  needsReviewItems: ChecklistItem[];
  /** The remaining items — the collapsible "Other documents" list. */
  otherItems: ChecklistItem[];
}

const maxDate = (
  a: string | null,
  b: string | null | undefined
): string | null => {
  if (b == null) {
    return a;
  }
  if (a == null) {
    return b;
  }

  return dayjs(b).isAfter(dayjs(a)) ? b : a;
};

/**
 * Groups the overview document summaries into SkySlope checklist items, then
 * splits them into the needs-review and other buckets the rail renders.
 *
 * Order follows the incoming `documentUpdatedAt` desc order: each item is
 * positioned at its first (most-recent) member, and documents stay in their
 * incoming order within an item.
 */
export const buildChecklistItems = (
  documents: ComplianceDocumentSummary[]
): ChecklistItems => {
  const itemById = new Map<string, ChecklistItem>();
  const items: ChecklistItem[] = [];

  for (const document of documents) {
    // A document belongs to a real SkySlope checklist activity only when it has
    // BOTH an activity id (the grouping key) and a non-blank activity name.
    // A blank name (SkySlope can return `''`) is treated as not-on-checklist —
    // each such document is its own single-document item titled by its name.
    const activityName = document.skyslopeActivityName?.trim();
    const hasActivity =
      document.skyslopeActivityId != null &&
      activityName != null &&
      activityName !== '';

    // Group by checklist activity when present; otherwise the document is its
    // own single-document item keyed by its id.
    const id = hasActivity
      ? document.skyslopeActivityId!
      : document.ingestionDocumentId;

    let item = itemById.get(id);
    if (!item) {
      item = {
        id,
        title: hasActivity ? activityName : document.documentName,
        isChecklistItem: hasActivity,
        docs: [],
        needsReview: false,
        resubmittedAt: null,
      };
      itemById.set(id, item);
      items.push(item);
    }

    item.docs.push(document);
    item.needsReview = item.needsReview || document.needsReviewByAgent;
    item.resubmittedAt = maxDate(
      item.resubmittedAt,
      document.agentResubmittedAt
    );
  }

  const otherItems = items.filter((item) => !item.needsReview);

  return {
    items,
    needsReviewItems: items.filter((item) => item.needsReview),
    // Real checklist items first; standalone "not on checklist" documents sink
    // to the bottom (stable within each group).
    otherItems: [
      ...otherItems.filter((item) => item.isChecklistItem),
      ...otherItems.filter((item) => !item.isChecklistItem),
    ],
  };
};

/** Finds the checklist item whose documents contain the given document id. */
export const findItemForDocument = (
  items: ChecklistItem[],
  ingestionDocumentId: string | undefined
): ChecklistItem | undefined => {
  if (ingestionDocumentId == null) {
    return undefined;
  }

  return items.find((item) =>
    item.docs.some((doc) => doc.ingestionDocumentId === ingestionDocumentId)
  );
};
